/** State and actions shared by the workspace shell and its overlays. */
import { listAgentTemplates } from "@/api/agents";
import { getProfile } from "@/api/auth";
import { createDocFrameAtNextPosition } from "@/components/layout/create-doc-frame";
import { createTerminalFrame } from "@/components/layout/create-terminal-frame";
import { getDesktopLayoutVisibility } from "@/components/layout/desktop-layout-visibility";
import { useLayout } from "@/components/layout/layout-provider";
import {
  focusBelongsToOtherDisplay,
  shouldExitPresentationForDesktopMode,
} from "@/components/layout/master-layout-presentation-policy";
import { useMasterSwitcherContextMenuRenderers } from "@/components/layout/master-switcher-context-menus";
import { runFixCompanionAction } from "@/components/layout/fix-companion-action";
import { runRailPanModeAction } from "@/components/layout/rail-pan-mode-action";
import { useMasterLayoutData } from "@/components/layout/use-master-layout-data";
import { useOpenTaskRequest } from "@/components/layout/use-open-task-request";
import { useRailPanModeLabel } from "@/components/layout/use-rail-pan-mode-label";
import type { ImageAttachment } from "@/components/panels/image-preview-strip";
import { useComposerAttachmentLifetime } from "@/components/panels/use-composer-attachment-lifetime";
import { closeCursorInputLocal } from "@/desktop/cursor-input-dismissal";
import { ensureTaskboardFrame } from "@/desktop/onboarding/workspace-frames/ensure-taskboard-frame";
import { useActiveCompactFrameOverlayIds } from "@/desktop/use-compact-frame-overlay";
import { useAuth } from "@/hooks/use-auth";
import { useCanvasPanMode } from "@/hooks/use-canvas-pan-mode";
import { useDispatchUserMessage } from "@/hooks/use-dispatch-user-message";
import { useInputPrewarmIntent } from "@/hooks/use-input-prewarm-intent";
import { useDestinationDraft } from "@/hooks/use-destination-draft";
import { isTauri } from "@/lib/is-tauri";
import { exitPresentationMode } from "@/lib/presentation-mode";
import { useActivityStore } from "@/stores/activity-store";
import { useAgentStore } from "@/stores/agent-store";
import { useCanvasStore } from "@/stores/canvas-store";
import {
  usePresentationStore,
  usePresentationVariant,
} from "@/stores/presentation-store";
import { useFramesStore } from "@/stores/frames-store";
import { selectFramesByType } from "@/stores/frames-selectors";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { inputDestinationKey } from "@/lib/input-destination-key";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { translate } from "@/i18n";
import { layoutMessages } from "@/i18n/messages/layout";
import { useNotificationStore } from "@/stores/notification-store";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { useReviewModeStore } from "@/stores/review-mode-store";
import { useCallback, useEffect, useRef, useState } from "react";
import { useGlobalSearchOverlay } from "./use-global-search-overlay";

export function useMasterLayoutShell() {
  useInputPrewarmIntent();
  const { user, logout } = useAuth();
  const pushActivity = useActivityStore((s) => s.pushEntry);
  const layout = useLayout();
  const data = useMasterLayoutData();
  const switcherContextMenus = useMasterSwitcherContextMenuRenderers(
    data,
    layout,
  );
  const dispatchUserMessage = useDispatchUserMessage();

  const draftDestination = useInputBarTarget((state) =>
    inputDestinationKey({
      startNewConversation: state.startNewConversation,
      conversationId: state.target?.conversationId,
    }),
  );
  const [inputValue, setInputValue] = useDestinationDraft(draftDestination);
  // Mirror of inputValue for handlers that need to read the
  // latest text without re-creating themselves on every keystroke.
  const inputValueRef = useRef(inputValue);
  inputValueRef.current = inputValue;
  const onboardingStep = useOnboardingStore((s) => s.step);
  const [maxParallelAgents, setMaxParallelAgents] = useState<number>(3);
  const [attachedImages, setAttachedImages] = useState<ImageAttachment[]>([]);
  useComposerAttachmentLifetime(attachedImages, setAttachedImages);
  const desktopMode = useDesktopMode((s) => s.mode);
  const denkWorkspacePreview = useDesktopMode((s) => s.denkWorkspacePreview);
  const denkCanvasCapture = useDesktopMode((s) => s.denkCanvasCapture);
  const compactFrameOverlayActive =
    useActiveCompactFrameOverlayIds().length > 0;
  const isDesktopTauri = isTauri();
  const denkMode = isDesktopTauri && desktopMode === "denk";
  const {
    desktopCompactMode,
    showAppHeaderChrome,
    showCanvasLeftRail,
    showCenterHeaderChrome,
    showDesktopCanvasContent,
    showLeftHeaderIsland,
    showRightHeaderIsland,
  } = getDesktopLayoutVisibility({
    isDesktopTauri,
    desktopMode,
    compactFrameOverlayActive,
    denkWorkspacePreview,
    denkCanvasCapture,
  });
  // Disable input bar only when the companion is disconnected. A skipped
  // onboarding state is terminal, but it should not make a connected desktop
  // install look unable to spawn CLI sessions.
  const onboardingInProgress =
    onboardingStep !== "completed" && onboardingStep !== "skipped";
  const inputBarDisabled =
    data.companionStatus === "disconnected" && !onboardingInProgress;
  const currentProjectId = data.currentProject?.id ?? null;
  const {
    request: searchRequest,
    openId: searchOpenId,
    close: closeSearch,
  } = useGlobalSearchOverlay(currentProjectId);

  // Live status surfaced in the slash-command popup (`/max`, `/heart`).
  const [liveStatus, setLiveStatus] = useState<{
    maxParallel: number;
    heartbeatOn: boolean;
  }>({ maxParallel: 10, heartbeatOn: false });
  const liveStatusCallId = useRef(0);

  const refreshLiveStatus = useCallback(async () => {
    const callId = ++liveStatusCallId.current;
    try {
      const [profile, templates] = await Promise.all([
        getProfile(),
        listAgentTemplates(),
      ]);
      // Drop stale responses if a newer refresh started meanwhile.
      if (callId !== liveStatusCallId.current) return;
      setLiveStatus({
        maxParallel: profile.max_parallel_agents,
        heartbeatOn: templates.some((t) => t.adapter_config?.heartbeat_enabled),
      });
    } catch {
      // Leave previous status in place on transient failures.
    }
  }, []);

  useEffect(() => {
    void refreshLiveStatus();
  }, [refreshLiveStatus]);
  const storeZoomIn = useCanvasStore((s) => s.zoomIn);
  const storeZoomOut = useCanvasStore((s) => s.zoomOut);
  const isPanMode = useCanvasPanMode();
  const railPanModeActive = isPanMode;
  const railPanModeLabel = useRailPanModeLabel(isDesktopTauri);
  const togglePanMode = useCanvasStore((s) => s.togglePanMode);
  const notePlacementActive = useCanvasStore((s) => s.notePlacementActive);
  const exitNotePlacement = useCanvasStore((s) => s.exitNotePlacement);
  const closeReviewMode = useReviewModeStore((s) => s.closeReviewMode);
  const reviewModeOpen = useReviewModeStore((s) => s.isOpen);
  const presentationVariant = usePresentationVariant();
  const focusDisplayId = usePresentationStore((s) =>
    s.presentation?.variant === "focus" ? s.presentation.displayId : undefined,
  );
  const presentationRenderer = usePresentationStore(
    (s) => s.presentation?.renderer,
  );
  const reviewChromeActive = reviewModeOpen || presentationVariant === "review";

  useEffect(() => {
    if (
      shouldExitPresentationForDesktopMode(
        desktopMode,
        presentationVariant,
        denkCanvasCapture,
        presentationRenderer,
      )
    ) {
      if (focusBelongsToOtherDisplay(focusDisplayId, getWindowDisplayId())) {
        useDesktopMode.getState().closeWorkspace();
      } else {
        exitPresentationMode();
      }
    }
    if (desktopMode === "denk" && !denkCanvasCapture) {
      closeReviewMode({ restoreDesktopMode: false });
    }
  }, [
    closeReviewMode,
    denkCanvasCapture,
    desktopMode,
    focusDisplayId,
    presentationRenderer,
    presentationVariant,
  ]);

  // Task selection updates Details content only when the panel is already
  // open. Reopening a hidden panel here made onboarding show an empty detail
  // panel after the user explicitly hid it.

  const activeProjectId = data.currentProject?.id;
  const activeSpaceId = data.currentSpace?.id;
  const handleSelectFrame = data.handleSelectFrame;

  // Task board: find existing or create — let the backend pick the slot.
  // When position_x/position_y are omitted, backend create_frame() runs
  // _next_frame_position() (mcp_handlers.py) which lays the frame
  // into the two-area layout (control left, deliverables right).
  const handleTaskboard = useCallback(async () => {
    const projectId = activeProjectId;
    if (!projectId) return;

    // Check if a taskboard frame already exists
    const [existing] = selectFramesByType(
      useFramesStore.getState(),
      "taskboard",
    );
    if (existing) {
      handleSelectFrame(existing.frameId);
      return;
    }

    try {
      const frameId = await ensureTaskboardFrame(projectId);
      // Wait for React Flow to process the new node before panning
      requestAnimationFrame(() => {
        handleSelectFrame(frameId);
      });
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : translate(layoutMessages, "unknownError");
      useNotificationStore.getState().addSystemToast({
        id: `taskboard-create-fail-${Date.now()}`,
        level: "error",
        title: translate(layoutMessages, "taskBoardFailed"),
        description: msg,
      });
    }
  }, [activeProjectId, handleSelectFrame]);

  const handleTerminal = useCallback(() => {
    const projectId = activeProjectId;
    const spaceId = activeSpaceId;
    if (!projectId || !spaceId) return;
    void createTerminalFrame(projectId, spaceId);
  }, [activeProjectId, activeSpaceId]);

  const handleNote = useCallback(() => {
    const projectId = activeProjectId;
    const spaceId = activeSpaceId;
    if (!projectId || !spaceId) return;
    exitNotePlacement();
    void createDocFrameAtNextPosition(projectId, spaceId);
  }, [activeProjectId, activeSpaceId, exitNotePlacement]);

  useOpenTaskRequest();

  const handleReplayTour = useCallback(() => {
    if (!isTauri()) {
      void useOnboardingStore.getState().resetToOrient("manual");
      return;
    }
    void import("@/desktop/onboarding/restart-desktop-onboarding").then(
      ({ restartDesktopOnboarding }) => restartDesktopOnboarding("manual"),
    );
  }, []);

  const handleFixCompanion = useCallback(
    () => runFixCompanionAction(layout.openSettings),
    [layout],
  );

  const handleZoomIn = useCallback(() => storeZoomIn?.(), [storeZoomIn]);
  const handleZoomOut = useCallback(() => storeZoomOut?.(), [storeZoomOut]);
  const handleRailPanMode = useCallback(() => {
    runRailPanModeAction({
      denkMode,
      denkCanvasCaptureActive: denkCanvasCapture,
      toggleCanvasPanMode: () => togglePanMode(),
    });
  }, [denkCanvasCapture, denkMode, togglePanMode]);

  /* ---------- Add/Remove agent flow ---------- */
  const removeAgentFromProject = useAgentStore((s) => s.removeAgentFromProject);

  const handleOpenAgentSettings = useCallback(() => {
    layout.openSettings({ page: "projects" });
  }, [layout]);

  const handleOpenSettingsForAgent = useCallback(
    (agentId: string) => {
      const agent = data.inputAgents.find((a) => a.id === agentId);
      if (agent) data.handleSelectAgents(new Set([agentId]));
      layout.openSettings({ page: "projects" });
    },
    [data, layout],
  );

  const handleRemoveAgent = useCallback(
    async (agentId: string) => {
      if (!data.currentProject) return;
      await removeAgentFromProject(data.currentProject.id, agentId);
    },
    [data.currentProject, removeAgentFromProject],
  );

  // Name-keyed wrapper for the cursor stall chip's "Switch model"
  // button. The cursor only knows the agent's display name, so we
  // resolve it back to a project_agent id and reuse the same settings
  // flow the input-bar long-press uses.
  const handleOpenSettingsForAgentName = useCallback(
    (agentName: string) => {
      const target = (agentName ?? "").split(" · ")[0]?.toLowerCase();
      if (!target) return;
      const agent = data.inputAgents.find(
        (a) => a.name.toLowerCase() === target,
      );
      if (agent) handleOpenSettingsForAgent(agent.id);
    },
    [data.inputAgents, handleOpenSettingsForAgent],
  );

  const currentSpaceId = data.currentSpace?.id;

  // Close the cursor-anchored input bar whenever the active space
  // changes. The canvas store keeps `cursorInput` independent of the
  // space, so without this an open bar would persist visually after
  // the user switches workspaces. (`useCanvasStore.clear()` is not
  // invoked on space switch — see the canvas store reset hook.)
  // Local-only: this also fires on first mount, and the owner's first
  // mount can be triggered by a summon on a DIFFERENT display — a
  // broadcast clear here would kill that just-opened bar.
  useEffect(() => {
    closeCursorInputLocal();
  }, [currentSpaceId]);

  return {
    user,
    logout,
    pushActivity,
    layout,
    data,
    switcherContextMenus,
    dispatchUserMessage,
    inputValue,
    setInputValue,
    inputValueRef,
    maxParallelAgents,
    setMaxParallelAgents,
    attachedImages,
    setAttachedImages,
    desktopMode,
    denkWorkspacePreview,
    denkCanvasCapture,
    compactFrameOverlayActive,
    isDesktopTauri,
    denkMode,
    desktopCompactMode,
    showAppHeaderChrome,
    showCanvasLeftRail,
    showCenterHeaderChrome,
    showDesktopCanvasContent,
    showLeftHeaderIsland,
    showRightHeaderIsland,
    inputBarDisabled,
    currentProjectId,
    searchRequest,
    searchOpenId,
    closeSearch,
    liveStatus,
    refreshLiveStatus,
    railPanModeActive,
    railPanModeLabel,
    notePlacementActive,
    closeReviewMode,
    presentationVariant,
    reviewChromeActive,
    handleSelectFrame,
    handleTaskboard,
    handleTerminal,
    handleNote,
    handleReplayTour,
    handleFixCompanion,
    handleZoomIn,
    handleZoomOut,
    handleRailPanMode,
    handleOpenAgentSettings,
    handleOpenSettingsForAgent,
    handleRemoveAgent,
    handleOpenSettingsForAgentName,
    currentSpaceId,
  };
}
